import React from 'react';
import {
    FloppyDiskIcon,
    TreeEvergreenIcon,
    SparkleIcon,
    UserCircleIcon,
    ClockIcon,
    CubeIcon,
    CurrencyDollarIcon,
    LightningIcon,
    SunIcon,
} from '@phosphor-icons/react';
import { useViewerStore } from '../../../../../store/store';
import type { SelectedDesignForPricing } from '../../../../../store/oneButtonStore';
import { resetLayers } from '../../../../../helpers/storeHelpers';
import siteService from '../../../../../services/siteService';
import { siteCalculationState, SHADE_CALCULATIONS } from '../../../../../services/siteCalculationState';
import { setDeactivateRoofArrInStoreFromDesignData } from '../../Three-JS-Viewer/Controllers/Common';
import { checkAndSetSetbackOverrideFlag } from '../../Three-JS-Viewer/Controllers/PathwayControl';
import { LoadModelJson, Obstruction_Tree_Initial_Setup } from '../../Three-JS-Viewer/Controllers/Loader';
import { getSite } from '../../../../../hooks/useLoadOriginalSite';
import {
    DashboardAiCatalogListRow,
    type DashboardAiCatalogListRowStatusVariant,
} from './DashboardAiCatalogListRow';

interface DashboardSavedDesignsSectionProps {
    aiPanelTab: 'ai' | 'tree' | 'saved' | 'wishlist';
    designList: any[];
    selectedDesignIndex: number;
    sceneViewer: any;
    setIsDashboardOpen: (next: boolean | ((prev: boolean) => boolean)) => void;
    navigate: any;
    triggerPricingGridForDesign: (design: SelectedDesignForPricing) => void;
    theme: 'dark' | 'light';
    isDashboardOpen: boolean;
    savedDesignsViewMode: 'grid' | 'table';
    setSavedDesignsViewMode: (next: 'grid' | 'table') => void;
}

export const DashboardSavedDesignsSection: React.FC<DashboardSavedDesignsSectionProps> = ({
    aiPanelTab,
    designList,
    selectedDesignIndex,
    sceneViewer,
    setIsDashboardOpen,
    navigate,
    triggerPricingGridForDesign,
    theme,
    isDashboardOpen,
    savedDesignsViewMode,
    setSavedDesignsViewMode,
}) => {
    if (aiPanelTab !== 'saved') return null;

    const hasManual = designList?.length > 0;
    const getLocalTime = (isoString: string) => {
        if (!isoString) return '';
        const globalTime = new Date(isoString);
        const localTime = new Date(globalTime.getTime() - globalTime.getTimezoneOffset() * 60000);
        return localTime.toLocaleString();
    };

    const handleOpenSavedDesignIn3D = async (designIndex: number) => {
        if (!sceneViewer) return;
        const currentDesigns = useViewerStore.getState().designList as any[];
        if (!currentDesigns?.[designIndex]) return;
        const selected = currentDesigns[designIndex];

        useViewerStore.setState({
            isSiteReCentered: false,
            isDesignLoading: true,
            selectedSiteDesign: null,
            roofsAltered: [],
            selectedDesignIndex: designIndex,
        });
        (sceneViewer as any).mapView = true;
        resetLayers();

        currentDesigns.forEach((d: any) => {
            if (d.Active_Ind === false) {
                useViewerStore.setState({
                    designID: 0,
                    isInActiveDesign: true,
                });
            }
        });

        try {
            setIsDashboardOpen(false);
            const opId = sessionStorage.getItem('SalesForceId') || '';
            const designNo = selected.Design_No;
            const versionNo = selected.Version_No;
            const data = await siteService.retrieveSiteDesignDetailsWithDesignData(opId, designNo, versionNo);
            const designData = data.DesignDataList[0].Design_Data;

            useViewerStore.setState({
                sitejson: JSON.stringify(designData),
                isAiGeneratedDesign: selected.isAiGeneratedDesign ?? false,
                selectedLossesVendor: selected.MultipliersVendor,
            });

            setDeactivateRoofArrInStoreFromDesignData(
                currentDesigns,
                designIndex,
                JSON.parse(designData).deactivatedRoofIdArr ?? [],
            );
            checkAndSetSetbackOverrideFlag(designData);
            await LoadModelJson(sceneViewer, designData, 'glb');
            if (useViewerStore.getState().isTreeLabelVisible) {
                (sceneViewer as any).toggleTreeLabel();
            }
        } finally {
            useViewerStore.setState({
                isDesignLoading: false,
                isAiGeneratedDesign: false,
            });
            siteCalculationState.needsUpdate(SHADE_CALCULATIONS);
            siteCalculationState.useCalculations(SHADE_CALCULATIONS);
        }
    };

    const handleLoadSavedDesignPricing = (design: any) => {
        const systemSizeKw =
            typeof design.System_Size_kWdc === 'number' && Number.isFinite(design.System_Size_kWdc)
                ? design.System_Size_kWdc
                : 0;
        const annualKwh =
            (design as any).Annual_Production_Kwh ??
            (design as any).Estimated_Annual_Production_Kwh ??
            (design as any).estimated_Annual_Production_Kwh ??
            0;

        const pricingDesign: SelectedDesignForPricing = {
            designId: `manual-${design.Design_No}-${design.Version_No}`,
            categoryId: 'oneDRAW manual design',
            systemSizeKw,
            estimatedAnnualProductionKwh: typeof annualKwh === 'number' && Number.isFinite(annualKwh) ? annualKwh : 0,
            productionW: 0,
            modules: undefined,
            roofCount: undefined,
            layoutKey: `manual-${design.Design_No}-${design.Version_No}`,
        };
        triggerPricingGridForDesign(pricingDesign);
    };

    const handleOpenOriginalSiteIn3D = async () => {
        if (!sceneViewer) return;
        useViewerStore.setState({
            isDesignLoading: true,
            deactivatedRoofIdArr: [],
            selectedDesignIndex: -1,
        });
        Obstruction_Tree_Initial_Setup(sceneViewer);
        (sceneViewer as any).mapView = true;
        resetLayers();
        try {
            setIsDashboardOpen(false);
            useViewerStore.setState({
                isAiGeneratedDesign: false,
            });
            await getSite(navigate);
        } finally {
            useViewerStore.setState({
                isDesignLoading: false,
            });
        }
    };

    return (
        <div
            className={`editor-ai-designs-grid editor-ai-designs-grid--catalog obd-ai-tree-saved-catalog-wrap obd-theme-${theme}`}
        >
        <div
            className={`obd-card obd-card--saved-designs obd-theme-${theme}`}
            style={{
                padding: '20px 20px 16px',
                borderRadius: 16,
                background: 'transparent',
                border: 'none',
                boxShadow: 'none',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 14,
                }}
            >
                <span
                    style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: theme === 'dark' ? 'rgba(248,250,252,0.98)' : '#1e293b',
                    }}
                >
                    <FloppyDiskIcon
                        size={18}
                        weight="duotone"
                        style={{ marginRight: 8, verticalAlign: -3 }}
                    />
                    Saved designs
                </span>
                <div
                    className="editor-ai-view-toggle"
                    role="tablist"
                    aria-label="Saved designs view"
                >
                    <button
                        type="button"
                        className={`editor-ai-view-btn${savedDesignsViewMode === 'grid' ? ' editor-ai-view-btn--active' : ''}`}
                        role="tab"
                        aria-selected={savedDesignsViewMode === 'grid'}
                        onClick={() => setSavedDesignsViewMode('grid')}
                    >
                        Grid
                    </button>
                    <button
                        type="button"
                        className={`editor-ai-view-btn${savedDesignsViewMode === 'table' ? ' editor-ai-view-btn--active' : ''}`}
                        role="tab"
                        aria-selected={savedDesignsViewMode === 'table'}
                        onClick={() => setSavedDesignsViewMode('table')}
                    >
                        List
                    </button>
                </div>
            </div>
            {!hasManual && (
                <p
                    style={{
                        fontSize: 12,
                        color: theme === 'dark' ? 'rgba(148,163,184,0.9)' : '#64748b',
                        margin: 0,
                    }}
                >
                    No finalized designs yet. Finalize a design from the editor to see it here.
                </p>
            )}
            {hasManual && (
                <>
                    <div
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            marginBottom: 6,
                            color: theme === 'dark' ? '#e2e8f0' : '#0f172a',
                        }}
                    >
                        Finalized designs ({designList.length})
                    </div>
                    {savedDesignsViewMode === 'grid' ? (
                        <div
                            className="obd-saved-designs-layout"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: isDashboardOpen ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                                gap: 14,
                            }}
                        >
                            {designList.map((design: any, index: number) => {
                                const isActiveManual = selectedDesignIndex === index;
                                return (
                                    <div
                                        key={`${design.Design_No}-${design.Version_No}-${index}`}
                                        className={`obd-scenario-card${
                                            isActiveManual ? ' obd-scenario-card--active' : ''
                                        }`}
                                    >
                                        <div className="obd-scenario-card__head">
                                            <span className="obd-scenario-card__label">
                                                <TreeEvergreenIcon
                                                    size={14}
                                                    weight="duotone"
                                                    className="obd-scenario-card__icon"
                                                />
                                                Design {design.Design_No} · V{design.Version_No}
                                            </span>
                                            {design.IsAiGeneratedDesign && (
                                                <span style={{ fontSize: 11, color: '#16a34a' }}>
                                                    <SparkleIcon size={14} weight="fill" />
                                                    AI Design
                                                </span>
                                            )}
                                        </div>
                                        <div className="obd-scenario-card__body">
                                            <div className="editor-ai-design-card__catalog-metric-rows obd-saved-designs-card__catalog-metrics">
                                                <div className="editor-ai-design-card__catalog-metric-row">
                                                    <LightningIcon size={16} weight="duotone" className="editor-ai-design-card__catalog-metric-ic" aria-hidden />
                                                    <span className="editor-ai-design-card__catalog-metric-label">System size</span>
                                                    <span className="editor-ai-design-card__catalog-metric-value">
                                                        {design.System_Size_kWdc ?? 0} kW
                                                    </span>
                                                </div>
                                                <div className="editor-ai-design-card__catalog-metric-row">
                                                    <CubeIcon size={16} weight="duotone" className="editor-ai-design-card__catalog-metric-ic" aria-hidden />
                                                    <span className="editor-ai-design-card__catalog-metric-label">Modules</span>
                                                    <span className="editor-ai-design-card__catalog-metric-value">{design.ModulesCount ?? 0}</span>
                                                </div>
                                                <div className="editor-ai-design-card__catalog-metric-row">
                                                    <SunIcon size={16} weight="duotone" className="editor-ai-design-card__catalog-metric-ic" aria-hidden />
                                                    <span className="editor-ai-design-card__catalog-metric-label">Production</span>
                                                    <span className="editor-ai-design-card__catalog-metric-value">
                                                        {(() => {
                                                            const kwh = Math.round(
                                                                design.Production.ActualAnnual ??
                                                                (design as any).Estimated_Annual_Production_Kwh ??
                                                                0,
                                                            );
                                                            return kwh != null && Number.isFinite(kwh)
                                                                ? `${Number(kwh).toLocaleString()} kWh/yr`
                                                                : '—';
                                                        })()}
                                                    </span>
                                                </div>
                                            </div>
                                            <hr className="editor-ai-design-card__catalog-hr" />
                                            <div className="editor-ai-design-card__catalog-metric-rows">
                                                <div className="editor-ai-design-card__catalog-metric-row">
                                                    <UserCircleIcon size={16} weight="duotone" className="editor-ai-design-card__catalog-metric-ic" aria-hidden />
                                                    <span className="editor-ai-design-card__catalog-metric-label">User</span>
                                                    <span className="editor-ai-design-card__catalog-metric-value">{design.User_Created_Id}</span>
                                                </div>
                                                <div className="editor-ai-design-card__catalog-metric-row">
                                                    <ClockIcon size={16} weight="duotone" className="editor-ai-design-card__catalog-metric-ic" aria-hidden />
                                                    <span className="editor-ai-design-card__catalog-metric-label">Saved</span>
                                                    <span className="editor-ai-design-card__catalog-metric-value">
                                                        {getLocalTime(design.User_Created_Timestamp)}
                                                    </span>
                                                </div>
                                            </div>
                                            <hr className="editor-ai-design-card__catalog-hr" />
                                            <div className="obd-scenario-card__finance">
                                                <div className="obd-scenario-card__finance-row">
                                                    <span className="obd-scenario-card__finance-label">GOODLEAP (PPA · LOAN · LEASE)</span>
                                                    <span className="obd-scenario-card__finance-value">—</span>
                                                </div>
                                                <div className="obd-scenario-card__finance-row">
                                                    <span className="obd-scenario-card__finance-label">LIGHTREACH TOTALEPCPAYMENT</span>
                                                    <span className="obd-scenario-card__finance-value">—</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            className="obd-scenario-card__footer"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <button
                                                type="button"
                                                className="obd-scenario-card__footer-btn"
                                                onClick={() => void handleOpenSavedDesignIn3D(index)}
                                                title="Open in 3D"
                                            >
                                                <CubeIcon size={16} weight="duotone" />
                                            </button>
                                            <button
                                                type="button"
                                                className="obd-scenario-card__footer-btn"
                                                onClick={() => handleLoadSavedDesignPricing(design)}
                                                title="Load pricing"
                                            >
                                                <CurrencyDollarIcon size={16} weight="duotone" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                            <div
                                className={`obd-scenario-card${
                                    selectedDesignIndex === -1 ? ' obd-scenario-card--active' : ''
                                }`}
                            >
                                <div className="obd-scenario-card__head">
                                    <span className="obd-scenario-card__label">
                                        <TreeEvergreenIcon
                                            size={14}
                                            weight="duotone"
                                            className="obd-scenario-card__icon"
                                        />
                                        Original Site
                                    </span>
                                </div>
                                <div className="obd-scenario-card__body">
                                    <div className="obd-scenario-card__row">
                                        <span className="obd-scenario-card__key">Info</span>
                                        <span className="obd-scenario-card__val">
                                            Base imported design <br />(Aurora/ EagleView)
                                        </span>
                                    </div>
                                </div>
                                <div
                                    className="obd-scenario-card__footer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <button
                                        type="button"
                                        className="obd-scenario-card__footer-btn"
                                        onClick={() => void handleOpenOriginalSiteIn3D()}
                                        title="Open in 3D"
                                    >
                                        <CubeIcon size={16} weight="duotone" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className={`obd-ai-catalog-list obd-ai-catalog-list--saved obd-theme-${theme}`} role="list">
                            <div className="obd-ai-catalog-list__subhead">Finalized designs</div>
                            {designList.map((design: any, index: number) => {
                                const isActiveManual = selectedDesignIndex === index;
                                const kwh = Math.round(
                                    design.Production.ActualAnnual ??
                                    (design as any).Estimated_Annual_Production_Kwh ??
                                    0,
                                );
                                const prodLine =
                                    kwh != null && Number.isFinite(kwh)
                                        ? `${Number(kwh).toLocaleString()} kWh/yr`
                                        : '—';
                                const cardKey = `saved-${design.Design_No}-${design.Version_No}-${index}`;
                                const kw =
                                    typeof design.System_Size_kWdc === 'number' && Number.isFinite(design.System_Size_kWdc)
                                        ? design.System_Size_kWdc.toFixed(2)
                                        : String(design.System_Size_kWdc ?? '—');
                                const statusLabel = isActiveManual ? 'Active' : 'Saved';
                                let statusVariant: DashboardAiCatalogListRowStatusVariant = 'neutral';
                                if (isActiveManual) statusVariant = 'lime';

                                return (
                                    <DashboardAiCatalogListRow
                                        key={cardKey}
                                        theme={theme}
                                        variant="dashboard"
                                        rowIndex={index}
                                        cardKey={cardKey}
                                        emptyThumb={design.IsAiGeneratedDesign ? 'sparkle' : 'floppy'}
                                        label={`Design ${design.Design_No} · V${design.Version_No}`}
                                        summaryLine={`${design.User_Created_Id} · ${getLocalTime(design.User_Created_Timestamp)}`}
                                        prodLine={prodLine}
                                        detailLine={`${design.ModulesCount ?? 0} modules`}
                                        statusLabel={statusLabel}
                                        statusVariant={statusVariant}
                                        systemKw={kw}
                                        isSelected={isActiveManual}
                                        isSimulating={false}
                                        onSelectForPricing={() => handleLoadSavedDesignPricing(design)}
                                        onPricing={() => handleLoadSavedDesignPricing(design)}
                                        onOpen3D={() => void handleOpenSavedDesignIn3D(index)}
                                        trailing={
                                            design.IsAiGeneratedDesign ? (
                                                <span className="obd-saved-designs-ai-badge obd-saved-designs-ai-badge--catalog">
                                                    <SparkleIcon size={14} weight="fill" aria-hidden />
                                                    AI Design
                                                </span>
                                            ) : null
                                        }
                                    />
                                );
                            })}
                            <DashboardAiCatalogListRow
                                theme={theme}
                                variant="dashboard"
                                rowIndex={designList.length}
                                cardKey="saved-original-site"
                                emptyThumb="tree"
                                label="Original Site"
                                summaryLine="Base imported design (Aurora / EagleView)"
                                prodLine="—"
                                detailLine=""
                                statusLabel={selectedDesignIndex === -1 ? 'Active' : 'Reference'}
                                statusVariant={selectedDesignIndex === -1 ? 'lime' : 'neutral'}
                                systemKw=""
                                hideSystemKw
                                isSelected={selectedDesignIndex === -1}
                                isSimulating={false}
                                selectForPricingDisabled
                                pricingDisabled
                                hideDollarAction
                                onSelectForPricing={() => {}}
                                onPricing={() => {}}
                                onOpen3D={() => void handleOpenOriginalSiteIn3D()}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
        </div>
    );
};
